import { Traveler } from '@flysas-tech/book-value-stream-models';
import { Enum, Intersect, Literal, Number, Object, Optional, Partial, Static, String, Union } from '@sinclair/typebox';

import { BonusProgram } from './bonusProgram';
import { PAX_TYPE } from './pax';

export enum PassengerFieldError {
  INVALID_FIELD = 'invalid-field',
  FIRST_NAME_REQUIRED = 'first-name-required',
  FIRST_NAME_BAD_FORMAT = 'first-name-bad-format',
  LAST_NAME_REQUIRED = 'last-name-required',
  LAST_NAME_BAD_FORMAT = 'last-name-bad-format',
  MIDDLE_NAME_BAD_FORMAT = 'middle-name-bad-format',
  EMAIL_REQUIRED = 'email-required',
  EMAIL_BAD_FORMAT = 'email-bad-format',
  PHONE_REQUIRED = 'phone-required',
  PHONE_BAD_FORMAT = 'phone-bad-format',
  GENDER_REQUIRED = 'gender-required',
  GENDER_BAD_FORMAT = 'gender-bad-format',
  PAX_TYPE_BAD_FORMAT = 'pax-type-bad-format',
  DATE_OF_BIRTH_BAD_FORMAT = 'date-of-birth-bad-format',
  INFANT_ADULT_ASSOCIATION_REQUIRED = 'infant-adult-association-required',
  PRIMARY_PAX_REQUIRED = 'primary-pax-required',
  DATE_OF_BIRTH_REQUIRED = 'date-of-birth-required',
  DATE_OF_BIRTH_PASSENGER_TYPE_MISSMATCH = 'date-of-birth-passenger-type-missmatch',
  EB_INVALID = 'eb-invalid',
  FFP_INVALID = 'ffp-invalid',
  FFP_ACCOUNT_INVALID = 'ffp-account-invalid',
  FFP_NUMBER_INVALID = 'ffp-number-invalid',
  FFP_NAME_MATCH_INVALID = 'ffp-name-match-invalid',
  FFP_DUPLICATE = 'ffp-duplicate',
}

export type PassengerKey = keyof Traveler;

export const PassengerErrors = Partial(
  Object({
    firstName: Enum(PassengerFieldError),
    middleName: Enum(PassengerFieldError),
    lastName: Enum(PassengerFieldError),
    gender: Enum(PassengerFieldError),
    dateOfBirth: Enum(PassengerFieldError),
    email: Enum(PassengerFieldError),
    phoneNumber: Enum(PassengerFieldError),
    frequentTraveler: Enum(PassengerFieldError),
  })
);

export type PassengerErrors = Static<typeof PassengerErrors>;

export type Passenger = Static<typeof Passenger>;
export const Passenger = Object({
  id: String(),
  type: Union([
    Literal(PAX_TYPE.ADULT),
    Literal(PAX_TYPE.CHILD),
    Literal(PAX_TYPE.INFANT),
    Literal(PAX_TYPE.YOUTH),
    Literal(PAX_TYPE.UNACCOMPANIED_MINOR),
  ]),
  firstName: Optional(String()),
  middleName: Optional(String()),
  lastName: Optional(String()),
  gender: Optional(String()),
  dateOfBirth: Optional(String()),
  email: Optional(String()),
  phoneNumber: Optional(String()),
  bonusProgram: Optional(BonusProgram),
  ebNumber: Optional(String()),
  guardianId: Optional(String()),
  errors: Optional(PassengerErrors),
});

export type ValidatedPassenger = Static<typeof ValidatedPassenger>;
export const ValidatedPassenger = Intersect([
  Passenger,
  Object({
    firstName: String(),
    lastName: String(),
  }),
]);

export type PassengerCounts = Static<typeof PassengerCounts>;
export const PassengerCounts = Object({
  adults: Number(),
  children: Number(),
  infants: Number(),
  youths: Number(),
});
